'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { X, Cookie, Settings, Check } from 'lucide-react';
import { 
  getConsentState, 
  setConsentState, 
  hasUserConsented,
  type ConsentState 
} from '@/lib/consent';

export default function ConsentBanner() {
  const t = useTranslations('consent');
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<ConsentState>({
    analytics: false,
    marketing: false,
    functional: false,
    necessary: true,
  });

  useEffect(() => {
    if (!hasUserConsented()) {
      setIsVisible(true);
    } else {
      const currentConsent = getConsentState();
      setConsent(currentConsent);
    }
  }, []);

  const handleAcceptAll = () => {
    const newConsent: ConsentState = {
      analytics: true,
      marketing: true,
      functional: true,
      necessary: true,
    };
    setConsentState(newConsent);
    setConsent(newConsent);
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const newConsent: ConsentState = {
      analytics: false,
      marketing: false,
      functional: false,
      necessary: true,
    };
    setConsentState(newConsent);
    setConsent(newConsent);
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    setConsentState(consent);
    setIsVisible(false);
    setShowDetails(false);
  };

  const toggleConsent = (type: keyof ConsentState) => {
    if (type === 'necessary') return;
    setConsent(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!isVisible) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[9998]" />
      <div className="fixed bottom-0 left-0 right-0 md:bottom-4 md:left-4 md:right-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-t-lg md:rounded-lg shadow-2xl z-[9999] max-w-6xl mx-auto">
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Cookie className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                {t('title')}
              </h2>
            </div>
            <button
              onClick={() => setIsVisible(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label={t('close')}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {t('description')}
          </p>

          {!showDetails ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleAcceptAll}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                {t('acceptAll')}
              </button>
              <button
                onClick={handleRejectAll}
                className="px-6 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium rounded-lg transition-colors"
              >
                {t('rejectAll')}
              </button>
              <button
                onClick={() => setShowDetails(true)}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-white font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                {t('customize')}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      {t('necessary')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('necessaryDescription')}
                    </p>
                  </div>
                  <div className="ml-4">
                    <input
                      type="checkbox"
                      checked={consent.necessary}
                      disabled
                      className="w-5 h-5 text-blue-600 rounded cursor-not-allowed opacity-50"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      {t('analytics')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('analyticsDescription')}
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => toggleConsent('analytics')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        consent.analytics ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          consent.analytics ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      {t('marketing')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('marketingDescription')}
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => toggleConsent('marketing')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        consent.marketing ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          consent.marketing ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      {t('functional')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('functionalDescription')}
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => toggleConsent('functional')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        consent.functional ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          consent.functional ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSavePreferences}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  {t('savePreferences')}
                </button>
                <button
                  onClick={() => setShowDetails(false)}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-white font-medium rounded-lg transition-colors"
                >
                  {t('back')}
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('moreInfo')}{' '}
              <a
                href="/info/datenschutz"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                {t('privacyPolicy')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}